import { CacheStrategy } from '@rammetec/data-access';
import { addressFieldDescriptors } from './_addressFields.js';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { LIFECYCLE_FIELD_DESCRIPTORS } from './_lifecycleFields.js';
import { VERSION_FIELD_DESCRIPTOR } from './_versionField.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PARTY, PARTY_ROLE_LABELS, PARTY_TYPE_LABELS, PartyTypeValues, } from '../types/party.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for Party entity.
 * Stored in 'org/{orgId}/party'.
 */
export const partyDescriptor = {
    name: PARTY,
    label: 'Parceiro',
    domain: 'catalog',
    collectionPath: 'org/{orgId}/party',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Customer and/or supplier party (PF or PJ)',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        // NoCache: entidade versionada (spec 0045) — getById em cache devolve versão velha e anula a detecção de conflito.
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            description: 'Unique party identifier',
        },
        {
            name: 'code',
            example: 501,
            label: 'Código',
            type: 'number',
            required: true,
            unique: true,
            autoNumber: true,
            description: 'Numeric party code (catalog identifier; non-negative, capped at MAX_CODE_LARGE)',
        },
        {
            name: 'partyType',
            example: 'PJ',
            label: 'Tipo de pessoa',
            type: 'enum',
            required: true,
            enumValues: [...PartyTypeValues],
            enumLabels: PARTY_TYPE_LABELS,
            description: 'PF (pessoa física) or PJ (pessoa jurídica)',
        },
        {
            name: 'displayName',
            example: 'Aurora Confecções',
            label: 'Nome',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.NAME,
            description: 'Canonical display name (PF: civil name; PJ: tradeName or legalName)',
        },
        {
            name: 'roles',
            label: 'Papéis',
            type: 'array',
            arrayItemType: 'string',
            required: false,
            // Presentation only (formatAuditValue); registry warns enumLabels on non-enum.
            enumLabels: PARTY_ROLE_LABELS,
            description: 'Business roles: CUSTOMER and/or SUPPLIER',
        },
        {
            name: 'cpf',
            label: 'CPF',
            type: 'string',
            required: false,
            audit: 'names-only',
            // Identifica a pessoa — não se copia; unicidade não é aplicada hoje.
            copy: false,
            description: 'CPF (PF only) — stored digits-only',
        },
        {
            name: 'birthDate',
            label: 'Data de nascimento',
            type: 'date',
            required: false,
            audit: 'names-only',
            description: 'Birth date (PF only)',
        },
        {
            name: 'cnpj',
            example: '98765432000110',
            label: 'CNPJ',
            type: 'string',
            required: false,
            // Identifica a pessoa — não se copia; unicidade não é aplicada hoje.
            copy: false,
            description: 'CNPJ (PJ only) — stored normalized (digits and uppercase letters)',
        },
        {
            name: 'tradeName',
            example: 'Aurora Confecções',
            label: 'Nome fantasia',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NAME,
            description: 'Trade name (PJ only)',
        },
        {
            name: 'legalName',
            example: 'Confecções Aurora Ltda',
            label: 'Razão social',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NAME,
            description: 'Legal name (PJ only)',
        },
        {
            name: 'email',
            label: 'E-mail',
            type: 'string',
            required: false,
            audit: 'names-only',
            description: 'Contact e-mail',
        },
        {
            name: 'phone',
            label: 'Telefone',
            type: 'string',
            required: false,
            audit: 'names-only',
            description: 'Contact phone',
        },
        // Standard address block, reference mode (spec 0047 §4.1 / D22). The four
        // names Party already had are exactly the block's, on purpose (D20): adopting
        // it renames nothing here, so no orphan column is left behind — it only adds
        // number, complement, district and the two catalog ids.
        // `audit: 'names-only'` vem do próprio gerador (spec 0046): endereço de
        // pessoa física é dado pessoal, e o log registra que mudou sem copiar o
        // valor. Antes do bloco, os quatro campos soltos traziam a anotação aqui.
        ...addressFieldDescriptors(),
        {
            name: 'notes',
            example: 'Contato Paula Mendes — (47) 3355-4500 — compras@aurora.example — Brusque/SC',
            label: 'Observações',
            type: 'string',
            required: false,
            description: 'Free-text notes',
        },
        ...LIFECYCLE_FIELD_DESCRIPTORS,
        ...AUDIT_FIELD_DESCRIPTORS,
        VERSION_FIELD_DESCRIPTOR,
    ],
    // No address FK (D31): `addressCityId`/`addressStateId` hold IBGE codes, not references.
    foreignKeys: [...AUDIT_FOREIGN_KEYS],
};
//# sourceMappingURL=party.descriptor.js.map